<script lang="ts" setup>
  import axios from 'axios'
  import { ref, onMounted } from 'vue'
  import { captureMessage } from '@sentry/vue'
  import {
    baseRequestToGP,
    urlPayGp,
    urlGetOrderId,
    merchantName,
    GOOGLE_PAY_SCRIPT_ID,
    GOOGLE_PAY_SCRIPT_URL,
    DEFAULT_MERCHANT_ID,
    DEFAULT_GATEWAY_MERCHANT_ID,
    DEFAULT_GATEWAY,
    TOKEN_TYPE,
  } from './QuoteGooglePayButtonV2.constants'
  import { PayGPResponse } from '@/shared/types/api.types'
  import { useEnvironment } from '@/shared/composables/useEnvironment'

  interface Props {
    totalPrice?: number
    acceptance?: boolean
    quoteNumber?: string
    validateBeforePayment?: () => Promise<boolean>
  }

  interface Emits {
    (e: 'succeedPayment'): void
    (e: 'start-pay'): void
    (e: 'google-pay-enabled'): void
    (e: 'acceptance-error'): void
    (e: 'error', value: string): void
  }

  const props = withDefaults(defineProps<Props>(), {
    totalPrice: 0,
    acceptance: true,
    quoteNumber: '',
    validateBeforePayment: () => Promise.resolve(true),
  })

  const emit = defineEmits<Emits>()

  let paymentsClient: google.payments.api.PaymentsClient | null = null
  const orderId = ref('')
  const gateway = ref(DEFAULT_GATEWAY)
  const merchantId = ref(DEFAULT_MERCHANT_ID)
  const gatewayMerchantId = ref(DEFAULT_GATEWAY_MERCHANT_ID)
  const transactionState = ref(false)
  const { isDevelopment } = useEnvironment()

  const loadScript = () =>
    new Promise((resolve) => {
      const el = document.getElementById(GOOGLE_PAY_SCRIPT_ID)

      if (el) {
        resolve(true)
        return
      }

      const script = document.createElement('script')
      script.onload = () => {
        resolve(true)
      }
      script.async = true
      script.id = GOOGLE_PAY_SCRIPT_ID
      script.type = 'text/javascript'
      script.src = GOOGLE_PAY_SCRIPT_URL
      document.head.appendChild(script)
    })

  const fetchPrerequisites = async () => {
    safeCaptureMessage(`GPay request to: ${urlGetOrderId}`, {
      quoteNumber: props.quoteNumber,
    })

    const canProceed = await props.validateBeforePayment?.()
    if (!canProceed) {
      return
    }

    const { data } = await axios.get(urlGetOrderId)

    safeCaptureMessage(`GPay response from: ${urlGetOrderId}`, {
      ...data,
      quoteNumber: props.quoteNumber,
    })
    gateway.value = data.gateway
    gatewayMerchantId.value = data.gatewayMerchantId
    merchantId.value = data.merchantId
    orderId.value = data.orderid
  }

  const getGooglePaymentsClient = () => {
    if (paymentsClient === null) {
      paymentsClient = new window.google.payments.api.PaymentsClient({
        environment: isDevelopment.value ? 'TEST' : 'PRODUCTION',
        paymentDataCallbacks: {
          onPaymentAuthorized: processPayment,
        },
      })
    }
  }

  const getBaseCardPaymentMethod = (): google.payments.api.PaymentMethodSpecification => ({
    type: 'CARD',
    parameters: {
      allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
      allowedCardNetworks: ['MASTERCARD', 'VISA'],
    },
    tokenizationSpecification: {
      type: TOKEN_TYPE,
      parameters: {
        gateway: isDevelopment.value ? 'dibsy' : 'verygoodsecurity',
        gatewayMerchantId: isDevelopment.value ? '22050495' : 'ACkXpaU1qFuSz9ukJaAqjzg5',
      },
    },
  })

  const getGoogleIsReadyToPayRequest = (): google.payments.api.IsReadyToPayRequest => ({
    ...baseRequestToGP,
    allowedPaymentMethods: [getBaseCardPaymentMethod()],
  })

  const getCardPaymentMethod = (): google.payments.api.PaymentMethodSpecification => ({
    ...getBaseCardPaymentMethod(),
    tokenizationSpecification: getTokenizationSpecification(),
  })

  const getTokenizationSpecification =
    (): google.payments.api.PaymentMethodTokenizationSpecification => ({
      type: TOKEN_TYPE,
      parameters: {
        gateway: isDevelopment.value ? 'dibsy' : 'verygoodsecurity',
        gatewayMerchantId: isDevelopment.value ? '22050495' : 'ACkXpaU1qFuSz9ukJaAqjzg5',
      },
    })

  const googlePayInitialize = () => {
    getGooglePaymentsClient()

    if (!paymentsClient) return
    paymentsClient
      .isReadyToPay(getGoogleIsReadyToPayRequest())
      .then((response) => {
        if (response.result) {
          emit('google-pay-enabled')

          return addGooglePayButton()
        }
      })
      .catch(console.error)
  }

  const addGooglePayButton = () => {
    if (!paymentsClient) return

    const button = paymentsClient.createButton({
      onClick: onGooglePaymentButtonClicked,
      allowedPaymentMethods: [getCardPaymentMethod()],
      buttonSizeMode: 'fill',
      buttonColor: 'black',
      buttonType: 'short',
      buttonLocale: 'en',
    })
    document.getElementById('gp-btn')?.appendChild(button)
  }

  const onGooglePaymentButtonClicked = async () => {
    if (!props.acceptance) {
      return emit('acceptance-error')
    }

    const paymentDataRequest = getGooglePaymentDataRequest()
    paymentsClient?.loadPaymentData(paymentDataRequest).catch((err) => {
      console.error(err)
    })

    fetchPrerequisites().catch((e) => {
      safeCaptureMessage(`GPay failed response from: ${urlGetOrderId}`, {
        message: e.message ?? '',
        quoteNumber: props.quoteNumber,
      })
    })
  }

  const getGooglePaymentDataRequest = (): google.payments.api.PaymentDataRequest => ({
    ...baseRequestToGP,
    allowedPaymentMethods: [getCardPaymentMethod()],
    callbackIntents: ['PAYMENT_AUTHORIZATION'],
    transactionInfo: getGoogleTransactionInfo(),
    merchantInfo: {
      merchantName,
      merchantId: merchantId.value,
    },
  })

  const getGoogleTransactionInfo = (): google.payments.api.TransactionInfo => ({
    countryCode: 'QA',
    currencyCode: 'QAR',
    totalPriceStatus: 'FINAL',
    totalPrice: `${props.totalPrice.toString()}`,
  })

  const processPayment = (
    paymentData: google.payments.api.PaymentData
  ): Promise<google.payments.api.PaymentAuthorizationResult> => {
    const successResult: google.payments.api.PaymentAuthorizationResult = {
      transactionState: 'SUCCESS',
    }
    const failureResult: google.payments.api.PaymentAuthorizationResult = {
      transactionState: 'ERROR',
      error: {
        intent: 'PAYMENT_AUTHORIZATION',
        message: 'Payment processing failed',
        reason: 'PAYMENT_DATA_INVALID',
      },
    }

    if (transactionState.value) {
      safeCaptureMessage('Gpay repeat request', {
        quoteNumber: props.quoteNumber,
      })
      return Promise.resolve(successResult)
    }

    const formData = new FormData()
    const ACTION = 'paymenttoken'
    const token = JSON.stringify(paymentData.paymentMethodData)
    formData.append('action', ACTION)
    formData.append('orderid', orderId.value)
    formData.append('token', token)

    return new Promise<google.payments.api.PaymentAuthorizationResult>((resolve, reject) => {
      emit('start-pay')

      safeCaptureMessage(`GPay request to: ${urlPayGp}`, {
        action: ACTION,
        orderid: orderId.value,
        token,
        quoteNumber: props.quoteNumber,
      })

      fetch(urlPayGp, {
        method: 'POST',
        body: formData,
      })
        .then((res) => res.json())
        .then((resJson: PayGPResponse) => {
          safeCaptureMessage(`GPay response from: ${urlPayGp}`, {
            ...resJson,
            quoteNumber: props.quoteNumber,
          })

          if (resJson.success) {
            emit('succeedPayment')
            transactionState.value = true
            safeCaptureMessage('GPay payment success', {
              ...resJson,
              quoteNumber: props.quoteNumber,
            })
            return resolve(successResult)
          } else {
            safeCaptureMessage(`GPay payment processing failed request to ${urlPayGp}`, {
              ...resJson,
              quoteNumber: props.quoteNumber,
            })
            emit('error', `GPay payment processing failed request to ${urlPayGp}`)
            return reject(failureResult)
          }
        })
        .catch((e) => {
          safeCaptureMessage(`GPay network error request to ${urlPayGp}`, {
            msg: e.message,
            quoteNumber: props.quoteNumber,
          })
          emit('error', e.message ?? `GPay network error request to ${urlPayGp}`)
          return reject(failureResult)
        })
    })
  }

  const safeCaptureMessage = (
    message: string,
    extra: Partial<PayGPResponse> & {
      message?: string
      quoteNumber?: string
      action?: string
      orderid?: string
      token?: string
      msg?: string
    }
  ) => {
    try {
      captureMessage(message, {
        extra,
      })
    } catch {
      console.error('Error while captureMessage for ', message)
    }
  }

  onMounted(async () => {
    try {
      await loadScript()
      setTimeout(googlePayInitialize, 1000)
    } catch (error) {
      console.warn(error)
    }
  })
</script>

<template>
  <div id="gp-btn" class="google-pay" style="height: 64px"></div>
</template>

<style>
  .gpay-button-fill button {
    background-size: 66px;
    border-radius: 12px;
  }
</style>
